<template>
  <div class="lei-page">
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3">
        <div class="lei-top">
          <div>
            <div class="page-titlerow">
              <h2 class="lei-title">Live events</h2>
              <button type="button" class="page-helpbtn" :class="{ 'is-open': showHelp }" :aria-expanded="showHelp" @click="showHelp = !showHelp">
                <svg class="page-helpbtn__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>How this page works
              </button>
            </div>
            <div class="lei-sub">{{ summary }}</div>
          </div>
        </div>

        <div v-show="showHelp" class="page-help">
          <div class="page-help__sec">
            <div class="page-help__label">What is listed</div>
            <ul>
              <li>A match is listed when the current time is inside its start–end window, or when one of its active channels has status on (early start or overrun).</li>
              <li>The match must have at least one <b>active</b> channel to appear in <b>Live now</b>. Its active channels are shown in their channel order. Times are in your local time.</li>
              <li>A match that is inside its start–end window but has no active channels is listed lower down, under <b>Should be live, not configured</b>, with the reason: no channels are configured for it, or channels exist but none are active. The page summary counts these as "not configured".</li>
              <li>Every 30 seconds the match list and channel records reload from the backend, and the stream data and alarms reload from B-API (one request; B-API reads the encoders itself, so this page never polls them). Refresh does all of it now. Refreshing pauses while you drag or save.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Stream strip, in order</div>
            <ul>
              <li><b>Badge:</b> streaming, stopped, unreachable, not linked (no encoder or stream id on the channel) or loading. The status comes from B-API's last read of the encoder (every 30 seconds). If the encoder or B-API cannot be reached the last known values stay, marked stale with the time they were read.</li>
              <li><b>Stream name</b> and then the stats line: <b>bitrate · fps · uptime · speed · errors</b>. Speed 1.0x is real time.</li>
              <li><b>Encoder → origin</b> on the right is where the stream is read from.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Channel row</div>
            <ul>
              <li><b>status on / off</b> is the channel's own status. A warning appears when it disagrees with the encoder (status on but not streaming, or streaming with status off).</li>
              <li><b>Alarms</b> (red, under the channel and in the banner at the top) are B-API's open alarms, with the time they opened and the same text as the Slack message. An alarm disappears when B-API clears it.</li>
              <li><b>Configuration warnings</b> (an amber banner at the top, and under the channel) appear when the channel does not match its encoder stream: the output URL points to another channel, the output origin differs from the channel's origin, the stream name differs, or the origin or stream name is empty. They use the output URL B-API kept when it started, took over or saw a restart of the stream, or your last Refresh if that is newer.</li>
              <li><b>More info</b> shows the stream's inputs and outputs as B-API last read them, with the time. <b>Refresh</b> reads them now from the encoder; it is the only request this page sends to an encoder. The chart icon opens the Kibana dashboard.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Starting, stopping and order</div>
            <ul>
              <li>This page does not start or stop streams: B-API starts and stops them from the calendar. A <b>Not streaming</b> badge on the match means it should be live but nothing is running.</li>
              <li><b>Reorder</b> lets you drag the channels of one match and saves the order when you click Done.</li>
              <li>A banner at the top shows when B-API's calendar is not running (slave, waiting, disabled), has not ticked for a few minutes, or cannot be reached. <b>B-API dry-run</b> means it only logs what it would start or stop.</li>
            </ul>
          </div>
        </div>

        <div class="lei-bar">
          <div class="lei-tools">
            <span class="lei-count lei-mono">
              <template v-if="refreshPaused">refresh paused</template>
              <template v-else>next poll &middot; {{ countdown }}s</template>
            </span>
            <button type="button" class="lei-btn" @click="manualRefresh" :disabled="pollInFlight || refreshing || refreshPaused">Refresh</button>
            <span v-if="bapiState && bapiState.dryRun" class="lei-dry" title="B-API sends no start or stop to the encoders, it only logs them">B-API dry-run</span>
          </div>
          <div v-if="distinctEncoders.length" class="lei-encoders">
            <span v-for="enc in distinctEncoders" :key="enc" class="lei-enc" :class="'lei-enc--' + encoderTone(enc)">
              <span class="lei-enc__dot"></span><b>{{ enc }}</b> {{ encoderLabel(enc) }}
            </span>
          </div>
        </div>

        <div v-if="bapiNotice" class="lei-banner">{{ bapiNotice }}</div>

        <div v-if="alarmItems.length" class="lei-banner lei-banner--bad">
          <b>{{ alarmItems.length }} open {{ alarmItems.length === 1 ? 'alarm' : 'alarms' }} in B-API.</b>
          <div v-for="a in alarmItems" :key="a.key" class="lei-banner__item lei-banner__item--mono">since {{ a.since }} · {{ a.text }}</div>
        </div>

        <div v-if="configWarnings.length" class="lei-banner">
          <b>{{ configWarnings.length }} {{ configWarnings.length === 1 ? 'channel has' : 'channels have' }} a configuration problem.</b>
          <div v-for="w in configWarnings" :key="w.calChannelId" class="lei-banner__item">
            <b>{{ w.channelName }}</b> ({{ w.match }}): {{ w.issues.join(' ') }}
          </div>
        </div>

        <div v-for="enc in unreachableEncoders" :key="'down-' + enc" class="lei-banner">
          <b>{{ enc }}</b> unreachable. {{ staleNote(enc) }}
        </div>

        <div v-if="loadError" class="error-message mt-3">{{ loadError }}</div>
        <div v-if="loading" class="lei-muted mt-3">Loading...</div>

        <template v-else>
          <div class="lei-section">Live now</div>
          <div v-if="!liveMatches.length" class="lei-muted">
            {{ unconfiguredMatches.length ? 'No configured matches are live right now.' : 'No live matches right now.' }}
          </div>

          <div v-for="match in liveMatches" :key="match.calMatchId" class="lei-card">
            <div class="lei-card__head">
              <span class="lei-pill" :class="phaseOf(match) === 'post' ? 'lei-pill--tag' : 'lei-pill--live'">{{ phaseLabel(match) }}</span>
              <span v-if="shouldBeLiveIdle(match)" class="lei-pill lei-pill--warn" title="The match is in its time window but no stream is running">Not streaming</span>
              <span v-if="match.format" class="lei-pill lei-pill--tag">{{ match.format }}</span>
              <span class="lei-card__teams">{{ match.team1 }} v {{ match.team2 }}</span>
            </div>
            <div class="lei-card__meta">
              <span class="lei-card__meta-text">{{ leagueNameFor(match.calLeagueId) }} &middot; {{ formatRange(match) }}</span>
              <div class="lei-card__action">
                <button v-if="channelsFor(match).length > 1" type="button"
                        class="lei-btn lei-btn--sm" :class="{ 'lei-btn--on': isReordering(match) }"
                        :disabled="savingOrder || (reorderMatchId !== null && !isReordering(match))"
                        @click="isReordering(match) ? finishReorder(match) : startReorder(match)">
                  {{ isReordering(match) ? (savingOrder ? 'Saving…' : 'Done') : 'Reorder' }}
                </button>
              </div>
            </div>
            <div v-if="orderErrors[match.calMatchId]" class="lei-row__error">{{ orderErrors[match.calMatchId] }}</div>
            <div class="lei-rows">
              <div v-if="!channelsFor(match).length" class="lei-empty">No active channels configured.</div>
              <Draggable
                :model-value="channelsFor(match)"
                item-key="calChannelId"
                handle=".lei-drag:not(.lei-drag--off)"
                :disabled="!isReordering(match) || savingOrder"
                :animation="150"
                :scroll="true"
                :scroll-sensitivity="80"
                :fallback-tolerance="5"
                ghost-class="drag-ghost"
                @start="dragging = true"
                @end="dragging = false"
                @update:model-value="onReorder(match, $event)"
              >
                <template #item="{ element: ch }">
                  <LiveChannelRow
                    :channel="ch"
                    :channel-record="channelByName[ch.channelName] || null"
                    :channel-records-loaded="channelRecordsLoaded"
                    :encoder-state-entry="ch.encoder ? (encoderState[ch.encoder.trim()] || null) : null"
                    :reorder-mode="isReordering(match)"
                    :config-issues="configIssuesFor(ch)"
                    :feed="feedById[ch.calChannelId] || null"
                    @info-loaded="storeConfigMeta(ch, $event)"
                  />
                </template>
              </Draggable>
            </div>
          </div>
        </template>

        <template v-if="!loading && unconfiguredMatches.length">
          <div class="lei-section lei-section--gap">Should be live, not configured</div>
          <div v-for="match in unconfiguredMatches" :key="match.calMatchId" class="lei-card">
            <div class="lei-card__head">
              <span class="lei-pill lei-pill--warn">Not configured</span>
              <span v-if="match.format" class="lei-pill lei-pill--tag">{{ match.format }}</span>
              <span class="lei-card__teams">{{ match.team1 }} v {{ match.team2 }}</span>
            </div>
            <div class="lei-card__meta">
              <span class="lei-card__meta-text">{{ leagueNameFor(match.calLeagueId) }} &middot; {{ formatRange(match) }}</span>
            </div>
            <div class="lei-empty">{{ unconfiguredReason(match) }}</div>
          </div>
        </template>

        <div class="lei-foot">
          Times in your local time ({{ timezoneLabel }}).
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import AdminService from '@/services/admin-service';
import XaccelService from '@/services/xaccel-service';
import Header from '@/components/Header.vue';
import LiveChannelRow from '@/components/LiveChannelRow.vue';
import { feedWindowMs } from '@/utils/general-functions';
import { isStreamRunning, checkChannelConfig, sortChannelsByOrder, flattenPublicCalendar, encoderStateFromBapi } from '@/utils/bapi-functions';

const POLL_SECONDS = 30;
const BAPI_STALE_MS = 3 * 60 * 1000;   // no B-API tick for this long → its data and alarms are shown as old

export default {
  name: 'LiveEvents',
  components: { Header, LiveChannelRow, Draggable },
  beforeRouteLeave() {
    if (this.reorderMatchId !== null && this.hasPendingChanges) {
      if (!window.confirm('You have unsaved changes that will be lost if you leave. Continue?')) return false;
    }
  },
  data() {
    return {
      calLeaguesList: [],
      calMatchesList: [],
      calChannelsList: [],
      channelRecords: [],
      channelRecordsLoaded: false,
      loading: false,
      loadError: '',
      // GET /calendar/state from B-API: feeds with their alarms and URLs, system alarms, encoder stats
      bapiState: null,
      bapiError: '',
      bapiAt: null,
      countdown: POLL_SECONDS,
      pollInFlight: false,
      refreshing: false,
      // calMatchId of the match whose channels are being reordered (one at a time)
      reorderMatchId: null,
      dragging: false,
      savingOrder: false,
      // { [calMatchId]: calChannelId[] } - order shown right after a drop, until the saved channel records reload
      localOrder: {},
      orderErrors: {},
      showHelp: false,
      // { [calChannelId]: { meta, at } } - the row's last Refresh (read from the encoder), used by the setup check
      configMeta: {}
    };
  },
  computed: {
    // Per encoder, from B-API's last read of it. While B-API itself can't be reached, the last values stay, marked
    // unreachable (stale). { stats: Map|null, lastOkAt: Date|null, unreachable, pending, notRead }
    encoderState() {
      const out = {};
      for (const enc of this.distinctEncoders) {
        if (!this.bapiState) {
          out[enc] = { stats: null, lastOkAt: null, unreachable: !!this.bapiError, pending: !this.bapiError, notRead: !!this.bapiError };
          continue;
        }
        const entry = encoderStateFromBapi(this.bapiState.encoders?.[enc]);
        out[enc] = this.bapiError ? { ...entry, unreachable: true } : entry;
      }
      return out;
    },
    // B-API's record of each feed, by calChannelId (only feeds whose window is open, until their stop is confirmed)
    feedById() {
      const map = {};
      for (const f of this.bapiState?.feeds || []) map[f.cal.calChannelId] = f;
      return map;
    },
    // Why B-API's data may be missing or old; '' = all fine.
    bapiNotice() {
      if (this.bapiError) {
        return `B-API not reachable (${this.bapiError}).` + (this.bapiAt ? ` Showing what it sent at ${this.formatClock(this.bapiAt)}.` : ' No stream data or alarms.');
      }
      const s = this.bapiState;
      if (!s) return '';
      if (s.state !== 'RUNNING') return `B-API calendar is ${s.state.toLowerCase().replace(/_/g, ' ')}: no stream data or alarms from it.`;
      if (s.lastTickAt && Date.now() - Date.parse(s.lastTickAt) > BAPI_STALE_MS) {
        return `B-API calendar has not ticked since ${this.formatClock(new Date(s.lastTickAt))}: its data and alarms are old.`;
      }
      return '';
    },
    // Every open B-API alarm: BE and encoder ones first, then the feeds' (each also shown on its channel row).
    alarmItems() {
      const s = this.bapiState;
      if (!s) return [];
      const since = a => this.formatClock(new Date(a.openedAt));
      const out = [];
      for (const [key, a] of Object.entries(s.systemAlarms || {})) out.push({ key: 'sys-' + key, since: since(a), text: a.text });
      for (const f of s.feeds || []) {
        for (const [type, a] of Object.entries(f.alarms || {})) out.push({ key: f.cal.calChannelId + '-' + type, since: since(a), text: a.text });
      }
      return out;
    },
    refreshPaused() {
      return this.dragging || this.savingOrder;
    },
    timezoneLabel() {
      const off = -new Date().getTimezoneOffset();
      const abs = Math.abs(off);
      const hh = String(Math.floor(abs / 60)).padStart(2, '0');
      const mm = String(abs % 60).padStart(2, '0');
      return `UTC${off >= 0 ? '+' : '-'}${hh}:${mm}`;
    },
    leaguesById() {
      const map = {};
      for (const lg of this.calLeaguesList) map[lg.calLeagueId] = lg;
      return map;
    },
    channelByName() {
      const map = {};
      for (const c of this.channelRecords) {
        if (!map[c.channelName]) map[c.channelName] = c;
      }
      return map;
    },
    channelsByMatch() {
      const map = {};
      for (const ch of this.calChannelsList) {
        if (!ch.active) continue;
        (map[ch.calMatchId] = map[ch.calMatchId] || []).push(ch);
      }
      const orderOf = (ch) => {
        const n = this.channelByName[ch.channelName]?.orderNumber;
        return n !== null && n !== undefined ? n : Infinity;
      };
      for (const id of Object.keys(map)) {
        map[id] = sortChannelsByOrder(map[id]).sort((a, b) => {
          const oa = orderOf(a);
          const ob = orderOf(b);
          return oa === ob ? 0 : (oa < ob ? -1 : 1);
        });
        const local = this.localOrder[id];
        if (local) {
          const pos = (ch) => {
            const i = local.indexOf(ch.calChannelId);
            return i === -1 ? Infinity : i;
          };
          map[id] = map[id].slice().sort((a, b) => pos(a) - pos(b));
        }
      }
      return map;
    },
    // Active channels whose feed window (match start - lead .. match end + trail) is open now - the feed runs before the match
    // starts, and that is when viewers already arrive. Their encoders are polled even if the match is not live yet.
    feedWindowChannels() {
      const now = Date.now();
      const matchById = new Map(this.calMatchesList.map(m => [m.calMatchId, m]));
      return this.calChannelsList.filter(ch => {
        if (!ch.active || !ch.encoder) return false;
        const w = feedWindowMs(ch, matchById.get(ch.calMatchId));
        return !!w && w.start <= now && now < w.end;
      });
    },
    // Matches that should be live (inside their start-end window) or already have a running feed: the channel's live
    // flag is on or - inside its feed window - the encoder reports the stream running.
    shouldBeLiveMatches() {
      const now = new Date();
      const feedOpen = new Set(this.feedWindowChannels.map(ch => ch.calChannelId));
      return this.calMatchesList
        .filter(m => {
          if (!m.startTime) return false;
          if (new Date(m.startTime) <= now && now < new Date(this.effectiveEnd(m))) return true;
          return (this.channelsByMatch[m.calMatchId] || []).some(ch =>
            ch.status === true || (feedOpen.has(ch.calChannelId) && this.isRunning(ch)));
        })
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    },
    // Live first, then pre-match, then post-match; by start time within each.
    liveMatches() {
      const rank = { live: 0, pre: 1, post: 2 };
      return this.shouldBeLiveMatches
        .filter(m => (this.channelsByMatch[m.calMatchId] || []).length > 0)
        .map((m, i) => ({ m, i, r: rank[this.phaseOf(m)] }))
        .sort((a, b) => a.r - b.r || a.i - b.i)
        .map(x => x.m);
    },
    unconfiguredMatches() {
      return this.shouldBeLiveMatches.filter(m => !(this.channelsByMatch[m.calMatchId] || []).length);
    },
    liveChannels() {
      return this.liveMatches.flatMap(m => this.channelsByMatch[m.calMatchId] || []);
    },
    distinctEncoders() {
      const seen = new Set();
      for (const ch of [...this.liveChannels, ...this.feedWindowChannels]) {
        const enc = ch.encoder?.trim();
        if (enc) seen.add(enc);
      }
      return [...seen].sort();
    },
    unreachableEncoders() {
      return this.distinctEncoders.filter(enc => this.encoderState[enc]?.unreachable && !this.encoderState[enc]?.notRead);
    },
    streamingCount() {
      return this.liveChannels.filter(ch => this.isRunning(ch)).length;
    },
    summary() {
      const m = this.liveMatches.length;
      const c = this.liveChannels.length;
      const u = this.unconfiguredMatches.length;
      const base = `${m} live ${m === 1 ? 'match' : 'matches'} · ${c} ${c === 1 ? 'channel' : 'channels'} · ${this.streamingCount} streaming`;
      return u ? `${base} · ${u} not configured` : base;
    },
    configWarnings() {
      const out = [];
      for (const match of this.liveMatches) {
        for (const ch of this.channelsFor(match)) {
          const issues = this.configIssuesFor(ch);
          if (issues.length) {
            out.push({ calChannelId: ch.calChannelId, channelName: ch.channelName, match: `${match.team1} v ${match.team2}`, issues });
          }
        }
      }
      return out;
    },
    hasPendingChanges() {
      return Object.keys(this.localOrder).length > 0;
    }
  },
  watch: {
    liveMatches(list) {
      // The match being reordered ended or vanished: drop the reorder state so other matches unlock
      if (this.reorderMatchId !== null && !this.savingOrder
          && !list.some(m => String(m.calMatchId) === String(this.reorderMatchId))) {
        this.reorderMatchId = null;
      }
    }
  },
  created() {
    this.refreshAll();
  },
  mounted() {
    this._countdownTimer = setInterval(() => {
      if (document.hidden || this.refreshPaused) return;
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.countdown = POLL_SECONDS;
        this.refreshAll();
      }
    }, 1000);
    this._onVisibilityChange = () => {
      if (!document.hidden) this.countdown = 0;
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this._onBeforeUnload = (e) => {
      if (this.reorderMatchId !== null && this.hasPendingChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this._onBeforeUnload);
  },
  beforeUnmount() {
    clearInterval(this._countdownTimer);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
  },
  methods: {
    // Channel records (order, status, active, live views) linked to calendar channels by channelName.
    // Reloaded with every stats poll so live viewers stay current. Fails soft: until they load, the rows show no channel info.
    // A call made while one is in flight waits for it and then loads again, so a save is never missed.
    loadChannelRecords() {
      if (this._channelsPromise) {
        return this._channelsPromise.then(() => this.loadChannelRecords());
      }
      this._channelsPromise = AdminService.getChannelsList(undefined, false).then((list) => {
        this.channelRecords = list || [];
        this.channelRecordsLoaded = true;
      }).catch((err) => {
        console.error('LiveEvents: error loading channels', err);
      }).finally(() => {
        this._channelsPromise = null;
      });
      return this._channelsPromise;
    },
    loadMatches() {
      const firstLoad = !this.calMatchesList.length;
      this.loading = firstLoad;
      return AdminService.getPublicCalendar().then((calendar) => {
        const { leagues, matches, channels } = flattenPublicCalendar(calendar);
        this.calLeaguesList = leagues;
        this.calMatchesList = matches;
        this.calChannelsList = channels;
        this.loadError = '';
      }).catch(err => {
        console.error('LiveEvents: error loading matches', err);
        this.loadError = err.response?.data?.message || err.message || 'Failed to load matches.';
      }).finally(() => {
        this.loading = false;
      });
    },
    // One B-API request for everything it holds in memory (no encoder call behind it). On a failure the last
    // answer stays, shown as stale.
    loadBapiState() {
      if (this.pollInFlight) return Promise.resolve();
      this.pollInFlight = true;
      return XaccelService.getCalendarState().then((state) => {
        this.bapiState = state;
        this.bapiError = '';
        this.bapiAt = new Date();
      }).catch((err) => {
        console.error('LiveEvents: B-API calendar state failed', err);
        this.bapiError = err.message || 'request failed';
      }).finally(() => {
        this.pollInFlight = false;
      });
    },
    // One cycle: matches and channel records from the backend, stream data and alarms from B-API.
    refreshAll() {
      if (this.refreshing || this.pollInFlight) return Promise.resolve();
      this.refreshing = true;
      return Promise.all([this.loadMatches(), this.loadChannelRecords(), this.loadBapiState()])
        .finally(() => { this.refreshing = false; });
    },
    manualRefresh() {
      if (this.refreshing || this.pollInFlight) return;
      this.countdown = POLL_SECONDS;
      this.refreshAll();
    },
    statFor(ch) {
      const entry = ch.encoder ? this.encoderState[ch.encoder.trim()] : null;
      if (!entry || !entry.stats) return null;
      return entry.stats.get(String(ch.encStreamId || '').trim()) || null;
    },
    isRunning(ch) {
      const stat = this.statFor(ch);
      return !!stat && isStreamRunning(stat.status);
    },
    channelsFor(match) {
      return this.channelsByMatch[match.calMatchId] || [];
    },
    storeConfigMeta(ch, meta) {
      this.configMeta = { ...this.configMeta, [ch.calChannelId]: { meta: meta || {}, at: Date.now() } };
    },
    // The stream read the setup check uses: the row's last Refresh or what B-API kept on its record, whichever is
    // newer. null = neither (nothing is checked; no request is made for it).
    configMetaFor(ch) {
      const refreshed = this.configMeta[ch.calChannelId];
      const feed = this.feedById[ch.calChannelId];
      const bapiAt = feed && feed.urlsReadAt ? Date.parse(feed.urlsReadAt) : null;
      if (refreshed && (bapiAt === null || refreshed.at >= bapiAt)) return refreshed.meta;
      if (bapiAt === null) return null;
      const stat = this.statFor(ch);
      return { output: (feed.outputUrls || [])[0] || null, name: stat ? stat.streamName : null };
    },
    configIssuesFor(ch) {
      const meta = this.configMetaFor(ch);
      return meta ? checkChannelConfig(ch, meta) : [];
    },
    unconfiguredReason(match) {
      const total = this.calChannelsList.filter(ch => String(ch.calMatchId) === String(match.calMatchId)).length;
      if (!total) return 'No channels are configured for this match.';
      return `${total} ${total === 1 ? 'channel exists' : 'channels exist'} but none ${total === 1 ? 'is' : 'are'} active.`;
    },
    inWindow(match) {
      const now = new Date();
      return !!match.startTime && new Date(match.startTime) <= now && now < new Date(this.effectiveEnd(match));
    },
    // Feeds run lead/trail minutes around the match, so a card here can be before, in or after it.
    phaseOf(match) {
      const now = new Date();
      if (!match.startTime || now < new Date(match.startTime)) return 'pre';
      return now < new Date(this.effectiveEnd(match)) ? 'live' : 'post';
    },
    phaseLabel(match) {
      return { pre: 'Pre-match', live: 'Live', post: 'Post-match' }[this.phaseOf(match)];
    },
    shouldBeLiveIdle(match) {
      return this.inWindow(match) && !this.channelsFor(match).some(ch => this.isRunning(ch));
    },

    // Saves the dragged order of one match. Returns false if any update failed.
    async saveOrderFor(matchId) {
      const match = this.liveMatches.find(m => String(m.calMatchId) === String(matchId));
      if (!match) return true;
      const updates = [];
      this.channelsFor(match).forEach((ch, idx) => {
        const record = this.channelByName[ch.channelName];
        if (record && Number(record.orderNumber) !== idx + 1) {
          updates.push({ ...record, orderNumber: idx + 1, active: null, status: null });
        }
      });
      try {
        for (const record of updates) {
          await AdminService.updateChannel(record);
        }
        return true;
      } catch (err) {
        console.error('LiveEvents: saving order failed', err);
        this.orderErrors = { ...this.orderErrors, [matchId]: 'Could not save the new order.' };
        return false;
      }
    },

    isReordering(match) {
      return this.reorderMatchId !== null && String(this.reorderMatchId) === String(match.calMatchId);
    },
    startReorder(match) {
      this.orderErrors = { ...this.orderErrors, [match.calMatchId]: '' };
      this.reorderMatchId = match.calMatchId;
    },
    async finishReorder(match) {
      const matchId = match.calMatchId;
      if (!this.localOrder[matchId]) {
        this.reorderMatchId = null;
        return;
      }
      this.savingOrder = true;
      let ok = false;
      try {
        ok = await this.saveOrderFor(matchId);
      } finally {
        await this.loadChannelRecords();
        this.savingOrder = false;
        if (ok) {
          const rest = { ...this.localOrder };
          delete rest[matchId];
          this.localOrder = rest;
          this.reorderMatchId = null;
        }
      }
    },

    onReorder(match, newList) {
      const matchId = match.calMatchId;
      this.orderErrors = { ...this.orderErrors, [matchId]: '' };
      const hasChange = newList.some((ch, idx) => {
        const record = this.channelByName[ch.channelName];
        return record && Number(record.orderNumber) !== idx + 1;
      });
      if (!hasChange) {
        const rest = { ...this.localOrder };
        delete rest[matchId];
        this.localOrder = rest;
        return;
      }
      this.localOrder = { ...this.localOrder, [matchId]: newList.map(ch => ch.calChannelId) };
    },

    effectiveEnd(match) {
      return match.endTime || match.startTime;
    },
    leagueNameFor(calLeagueId) {
      const lg = this.leaguesById[calLeagueId];
      return lg ? lg.name : `League #${calLeagueId}`;
    },
    formatClock(date) {
      return date.toLocaleTimeString(undefined, { hour12: false });
    },
    formatLocalTime(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    },
    formatRange(match) {
      const start = this.formatLocalTime(match.startTime);
      return match.endTime ? `${start} – ${this.formatLocalTime(match.endTime)}` : start;
    },
    encoderTone(enc) {
      const entry = this.encoderState[enc];
      if (!entry || entry.pending) return 'loading';
      return entry.unreachable ? 'down' : 'ok';
    },
    encoderLabel(enc) {
      const entry = this.encoderState[enc];
      if (!entry || entry.pending) return 'loading…';
      if (entry.notRead) return 'no data from B-API';
      if (entry.unreachable) return entry.lastOkAt ? `unreachable · last ok ${this.formatClock(entry.lastOkAt)}` : 'unreachable';
      return `ok · updated ${this.formatClock(entry.lastOkAt)}`;
    },
    staleNote(enc) {
      const at = this.encoderState[enc]?.lastOkAt;
      return at
        ? `Showing last known values for its channels (updated ${this.formatClock(at)}).`
        : 'No values received yet.';
    }
  }
};
</script>
<style src="../../assets/style.css"></style>
